import type { Reservation } from '@/types';

export type NormalizableReservation = Partial<Reservation> & Record<string, unknown>;

type NormalizableInput = NormalizableReservation | Reservation;

const asString = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return undefined;
};

const asBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return undefined;
};

const firstNonEmpty = (
  ...values: Array<string | null | undefined>
): string => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return '';
};

/**
 * Extrae hora UTC en formato HH:mm
 */
function extractUtcTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const hours = d.getUTCHours().toString().padStart(2, '0');
  const minutes = d.getUTCMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Extrae fecha UTC en formato yyyy-MM-dd
 */
function extractUtcDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const year = d.getUTCFullYear();
  const month = (d.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = d.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Normaliza una reserva a la forma canónica usada en el frontend:
 * - commonAreaId (con fallback a resourceId o area_id)
 * - date: 'yyyy-MM-dd' (UTC)
 * - startTime / endTime: 'HH:mm' (UTC)
 * - status y createdAt con valores por defecto si faltan
 * - paid con valor por defecto si falta
 */
export function normalizeReservation(raw: NormalizableInput): Reservation {
  const record = raw as NormalizableReservation;

  const commonAreaId =
    raw.commonAreaId ?? asString(record.resourceId) ?? asString(record.area_id);

  const startIso =
    raw.startTime ?? asString(record.start_time) ?? asString(record.start_at);
  const endIso = raw.endTime ?? asString(record.end_time) ?? asString(record.end_at);

  const statusValue =
    typeof raw.status === 'string'
      ? raw.status
      : asString(record.status) ?? 'confirmed';
  const paymentId = raw.paymentId ?? asString(record.payment_id) ?? null;
  const paidValue =
    typeof raw.paid === 'boolean' ? raw.paid : asBoolean(record.paid) ?? false;

  const normalized: Reservation = {
    id: raw.id ?? asString(record.id) ?? '',
    userId: raw.userId ?? asString(record.user_id) ?? '',
    commonAreaId: asString(commonAreaId) ?? '',
    resourceId: raw.resourceId ?? asString(record.resourceId),
    date: firstNonEmpty(extractUtcDate(startIso ?? ''), raw.date, asString(record.date)),
    startTime: firstNonEmpty(raw.startTime, extractUtcTime(startIso ?? '')),
    endTime: firstNonEmpty(raw.endTime, extractUtcTime(endIso ?? '')),
    status: statusValue,
    createdAt:
      typeof raw.createdAt === 'string'
        ? raw.createdAt
        : asString(record.created_at) ?? new Date().toISOString(),
    paid: paidValue,
    paymentId,
  };

  return normalized;
}

/**
 * Normaliza y deduplica por id.
 */
export function normalizeMany(
  list: Array<NormalizableReservation | Reservation> | undefined | null
): Reservation[] {
  const out: Reservation[] = [];
  const seen = new Set<string>();
  if (!list) return out;
  for (const r of list) {
    const n = normalizeReservation(r);
    if (!n.id) continue;
    if (seen.has(n.id)) continue;
    seen.add(n.id);
    out.push(n);
  }
  return out;
}

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function overlaps(
  aStartMin: number,
  aEndMin: number,
  bStartMin: number,
  bEndMin: number
): boolean {
  return aStartMin < bEndMin && bStartMin < aEndMin;
}

export function resourceSlotKey(
  resourceId: string,
  start: string,
  end: string
): string {
  return `${resourceId}|${start}|${end}`;
}
